import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatIcon from '@mui/icons-material/Chat';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import HistoryIcon from '@mui/icons-material/History';

// The account menu as data, shared by the desktop user menu (and AdminMenu)
// and the phone More sheet. Pure: no hooks here (useMenuEntries.ts reads the
// current user), so AdminMenu can import it without loading Firebase.
export type MenuGroup = 'main' | 'admin';

export interface MenuEntry {
    id: string;
    label: string;
    to?: string;
    icon: ComponentType<SvgIconProps>;
    group: MenuGroup;
    disabled?: boolean;
}

const MAIN_ENTRIES: readonly MenuEntry[] = [
    {
        id: 'settings',
        label: 'Settings',
        to: '/settings',
        icon: SettingsIcon,
        group: 'main',
    },
    {
        id: 'chats',
        label: 'Chats',
        to: '/chats',
        icon: ChatIcon,
        group: 'main',
    },
];

const OPERATOR_GATES: MenuEntry = {
    id: 'gates',
    label: 'Gates',
    to: '/gates',
    icon: GavelIcon,
    group: 'main',
};

export const ADMIN_ENTRIES: readonly MenuEntry[] = [
    {
        id: 'admin-users',
        label: 'Manage Users',
        to: '/admin/users',
        icon: PeopleIcon,
        group: 'admin',
    },
    {
        id: 'admin-subscribers',
        label: 'Subscribers (soon)',
        icon: SubscriptionsIcon,
        group: 'admin',
        disabled: true,
    },
    {
        id: 'admin-gates',
        label: 'Gates',
        to: '/gates',
        icon: GavelIcon,
        group: 'admin',
    },
    {
        id: 'admin-audit',
        label: 'Audit Log',
        to: '/admin/audit',
        icon: HistoryIcon,
        group: 'admin',
    },
];

export interface MenuAccess {
    /** AdminMenu's rule: permissions === 'admin'. */
    showAdminGroup: boolean;
    /** GatesMenu's rule: assigned gate operator who is not an admin; admins
     *  get Gates in the admin group instead, so the link never appears twice. */
    showOperatorGates: boolean;
}

export function menuEntriesFor({
    showAdminGroup,
    showOperatorGates,
}: MenuAccess): MenuEntry[] {
    return [
        ...MAIN_ENTRIES,
        ...(showOperatorGates ? [OPERATOR_GATES] : []),
        ...(showAdminGroup ? ADMIN_ENTRIES : []),
    ];
}
